import React, { createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { User } from './types';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LoanDetail from './pages/LoanDetail';
import NewLoan from './pages/NewLoan';
import AdminRules from './pages/AdminRules';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);
export const useAuthContext = () => useContext(AuthContext);

function ProtectedRoute({ children, reviewerOnly }: { children: React.ReactNode; reviewerOnly?: boolean }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0F1117]">
        <div className="text-[#2563EB] text-xl font-mono">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (reviewerOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  const auth = useAuth();

  if (auth.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0F1117]">
        <div className="text-[#2563EB] text-xl font-mono">Initializing...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={auth.user ? <Navigate to="/dashboard" replace /> : <Login />} />
          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/loans/new" element={
            <ProtectedRoute><NewLoan /></ProtectedRoute>
          } />
          <Route path="/loans/:id" element={
            <ProtectedRoute><LoanDetail /></ProtectedRoute>
          } />
          <Route path="/admin/rules" element={
            <ProtectedRoute reviewerOnly><AdminRules /></ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
